import type {
  AiPreviewData,
  AiPreviewScreen,
  PreviewData,
} from '@/features/campaigns/types/campaign';
import type { AiPreviewTimeSlot } from './AIPreview.types';

type SlotBillboard = { billboardId?: string; _id?: string; id?: string; repetitions?: number; cost?: number };

export type PlaySlot = { billboardId: string; hour: number; date: string; repetitions: number };

const slotBillboardId = (b: SlotBillboard | string) =>
  typeof b === 'string' ? b : String(b.billboardId || b._id || b.id || '');

const slotEntries = (slot: AiPreviewTimeSlot) =>
  (Array.isArray(slot.billboards) ? slot.billboards : []) as Array<SlotBillboard | string>;

const dayOf = (slot: AiPreviewTimeSlot) => String(slot.date).slice(0, 10);

/** Number of campaign days the plan runs on. */
export function planDays(plan: AiPreviewData) {
  const dates = new Set((plan.timeSlots ?? []).map(dayOf));
  return Math.max(1, dates.size);
}

/** The plan flattened to one row per screen, date and hour. */
function planSlots(plan: AiPreviewData): PlaySlot[] {
  const rows: PlaySlot[] = [];
  for (const slot of plan.timeSlots ?? []) {
    for (const entry of slotEntries(slot)) {
      const repetitions = typeof entry === 'string' ? 0 : Number(entry.repetitions ?? 0);
      if (repetitions <= 0) continue;
      rows.push({
        billboardId: slotBillboardId(entry),
        hour: Number(slot.hour),
        date: dayOf(slot),
        repetitions,
      });
    }
  }
  return rows;
}

/** Plays per day the AI planned for each screen (averaged over the campaign days). */
export function playsPerDayByScreen(plan: AiPreviewData): Record<string, number> {
  const totals: Record<string, number> = {};
  for (const row of planSlots(plan)) {
    totals[row.billboardId] = (totals[row.billboardId] ?? 0) + row.repetitions;
  }
  const days = planDays(plan);
  return Object.fromEntries(Object.entries(totals).map(([id, total]) => [id, Math.round(total / days)]));
}

/**
 * The booking slots for the plays per day the user chose. Each day keeps the hours the AI picked.
 * Fewer plays are taken from the smallest hours first. Extra plays top up the busiest hours up to
 * what is free there, then spill onto the nearest other hours the screen has free.
 */
export type HourCapacity = Record<string, Record<string, number>>;

export function bookingSlotsForPlays(
  plan: AiPreviewData,
  playsById: Record<string, number>,
  capacity: HourCapacity = {},
): PlaySlot[] {
  const rows = planSlots(plan);
  const slots: PlaySlot[] = [];

  for (const [billboardId, target] of Object.entries(playsById)) {
    if (target <= 0) continue;
    const byDate = new Map<string, PlaySlot[]>();
    for (const row of rows.filter((r) => r.billboardId === billboardId)) {
      byDate.set(row.date, [...(byDate.get(row.date) ?? []), { ...row }]);
    }

    for (const entries of byDate.values()) {
      let diff = target - entries.reduce((sum, e) => sum + e.repetitions, 0);
      if (diff > 0) {
        const free = capacity[billboardId];
        const freeAt = (hour: number) => (free ? Number(free[hour] ?? 0) : Number.POSITIVE_INFINITY);
        const date = entries[0].date;
        const byBusiest = [...entries].sort((a, b) => b.repetitions - a.repetitions);

        for (const entry of byBusiest) {
          const add = Math.min(diff, Math.max(0, freeAt(entry.hour) - entry.repetitions));
          entry.repetitions += add;
          diff -= add;
        }
        if (diff > 0 && free) {
          const used = new Set(entries.map((e) => e.hour));
          const anchor = byBusiest[0].hour;
          const otherHours = Object.keys(free).map(Number).filter((h) => !used.has(h))
            .sort((a, b) => Math.abs(a - anchor) - Math.abs(b - anchor) || a - b);
          for (const hour of otherHours) {
            if (diff <= 0) break;
            const add = Math.min(diff, freeAt(hour));
            entries.push({ billboardId, hour, date, repetitions: add });
            diff -= add;
          }
        }
      } else if (diff < 0) {
        for (const entry of [...entries].sort((a, b) => a.repetitions - b.repetitions)) {
          const cut = Math.min(entry.repetitions, -diff);
          entry.repetitions -= cut;
          diff += cut;
          if (diff === 0) break;
        }
      }
      slots.push(...entries.filter((e) => e.repetitions > 0));
    }
  }
  return slots;
}

/**
 * Folds a server-priced `step3` result into the AI plan so the UI keeps reading one shape:
 * the screens and slots are the ones just saved, and every total comes from the server.
 */
export function applyStep3ToPlan(
  plan: AiPreviewData,
  slots: PlaySlot[],
  step3: PreviewData,
  budget: number | null,
): AiPreviewData {
  const keptIds = new Set(slots.map((s) => s.billboardId));

  const pricingByScreen = new Map<string, Record<string, unknown>>();
  for (const row of (step3.billboardWise ?? []) as Array<Record<string, unknown>>) {
    const id = String((row.billboardId as { _id?: string } | undefined)?._id ?? row.billboardId ?? '');
    if (id) pricingByScreen.set(id, row);
  }

  const keep = (screen: AiPreviewScreen): boolean => keptIds.has(String(screen._id));
  const withPricing = (screen: AiPreviewScreen): AiPreviewScreen => {
    const row = pricingByScreen.get(String(screen._id));
    if (!row) return screen;

    // With a coupon the server returns each screen's row already reduced by its share of the
    // discount. Screens are shown at their full price (the coupon is shown once, on the bill), so
    // put the discount back and keep the same GST rate.
    const reduced = Number(row.amountWithoutTax ?? 0);
    const base = reduced + Number(row.discountAmount ?? 0);
    const taxRate = reduced > 0 ? Number(row.totalTax ?? 0) / reduced : 0;
    return {
      ...screen,
      pricing: {
        ...screen.pricing,
        amountWithoutTax: base,
        totalTax: base * taxRate,
        amountWithTax: base * (1 + taxRate),
        totalRepetitions: Number(row.assignedRepetitions ?? screen.pricing?.totalRepetitions ?? 0),
      },
    };
  };

  const costPerPlay = (id: string) => {
    const row = pricingByScreen.get(id);
    const reps = Number(row?.assignedRepetitions ?? 0);
    return reps > 0 ? Number(row?.amountWithoutTax ?? 0) / reps : 0;
  };

  const grouped = new Map<string, AiPreviewTimeSlot>();
  for (const slot of slots) {
    const key = `${slot.date}|${slot.hour}`;
    const group = grouped.get(key) ?? { date: slot.date, hour: slot.hour, billboards: [], totalRepetitions: 0, totalCost: 0 };
    const cost = costPerPlay(slot.billboardId) * slot.repetitions;
    (group.billboards as SlotBillboard[]).push({ billboardId: slot.billboardId, repetitions: slot.repetitions, cost });
    group.totalRepetitions = Number(group.totalRepetitions) + slot.repetitions;
    group.totalCost = Number(group.totalCost) + cost;
    grouped.set(key, group);
  }
  const timeSlots = [...grouped.values()].sort(
    (a, b) => String(a.date).localeCompare(String(b.date)) || Number(a.hour) - Number(b.hour),
  );

  const rawSummary = (step3.summary ?? {}) as NonNullable<AiPreviewData['summary']>;
  const payable = Number(step3.pricing?.totalAmount ?? rawSummary.amountWithTax ?? 0);
  const screens = (plan.screens ?? []).filter(keep).map(withPricing);

  // The payable amount is the one figure the server is sure about. Rebuild the rest from it in the
  // same shape an AI plan has: amount before GST (after any coupon), GST on that, and the total.
  const base = Number(rawSummary.totalBaseAmount ?? rawSummary.amountWithoutTax ?? 0);
  const discount = Number(rawSummary.discountAmount ?? 0);
  const summary: NonNullable<AiPreviewData['summary']> = {
    ...rawSummary,
    totalBaseAmount: base,
    discountAmount: discount,
    amountWithoutTax: base - discount,
    totalTax: Math.max(0, payable - (base - discount)),
    amountWithTax: payable,
  };

  return {
    ...plan,
    screens,
    screensDetail: (plan.screensDetail ?? []).filter(keep).map(withPricing),
    timeSlots,
    pendingSave: false,
    // The coupon the server actually applied, so the page knows what is saved.
    coupon: step3.coupon ?? plan.coupon,
    summary: { ...plan.summary, ...summary, totalScreens: screens.length },
    estimatedCost: {
      ...plan.estimatedCost,
      total: payable,
      finalPayableAmount: payable,
      baseAmount: base,
      amountWithoutTax: base - discount,
      totalTax: summary.totalTax,
      discountAmount: discount,
      taxDistribution: summary.taxDistribution,
      remaining: budget != null ? Math.max(0, budget - payable) : plan.estimatedCost?.remaining,
    },
  } as AiPreviewData;
}

/** The dates the plan runs on, as `YYYY-MM-DD`. */
export function planDates(plan: AiPreviewData): string[] {
  return [...new Set((plan.timeSlots ?? []).map(dayOf))].sort();
}

/**
 * Adds a screen the user picked to the plan on every campaign day at one hour. The price on the
 * screen is an estimate until the server saves and reprices the plan (`pendingSave`).
 */
export function addScreenToPlan(
  plan: AiPreviewData,
  screen: AiPreviewScreen,
  hour: number,
  playsPerDay: number,
): AiPreviewData {
  const dates = planDates(plan);
  const costPerPlay = Number(screen.pricing?.amountWithoutTax ?? 0) / Math.max(1, Number(screen.pricing?.totalRepetitions ?? 1));

  const timeSlots = [...(plan.timeSlots ?? [])];
  for (const date of dates) {
    const entry = { billboardId: String(screen._id), repetitions: playsPerDay, cost: costPerPlay * playsPerDay };
    const index = timeSlots.findIndex((slot) => dayOf(slot) === date && Number(slot.hour) === hour);
    if (index >= 0) {
      const slot = timeSlots[index];
      timeSlots[index] = {
        ...slot,
        billboards: [...slotEntries(slot), entry] as AiPreviewTimeSlot['billboards'],
        totalRepetitions: Number(slot.totalRepetitions ?? 0) + playsPerDay,
        totalCost: Number(slot.totalCost ?? 0) + entry.cost,
      };
    } else {
      timeSlots.push({ date, hour, billboards: [entry], totalRepetitions: playsPerDay, totalCost: entry.cost });
    }
  }

  return {
    ...plan,
    screens: [...(plan.screens ?? []), screen],
    screensDetail: [...(plan.screensDetail ?? []), screen],
    timeSlots,
    pendingSave: true,
  };
}
